import {
  Box,
  Button,
  Center,
  createStyles,
  List,
  Paper,
  Text,
  Title,
} from "@mantine/core";
import React from "react";

const JEM_LOGO_URL =
  "https://imagedelivery.net/THJz7cQLDDpzn89AEYAkbg/dc5010fa-b9ce-4854-e5ed-f7ba2c9ca300/public";

const useStyles = createStyles((theme) => ({
  loginCard: {
    padding: "68px 40px",
    borderRadius: "20px",
    width: "100%",
    maxWidth: 470,
    minHeight: 480,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.white,
    "@media (max-height: 800px)": {
      padding: "30px",
    },
  },
  logo: {
    height: "56px",
    width: "auto",
    "@media (max-height: 800px)": {
      height: "40px",
    },
  },
  logoContainer: {
    marginBottom: "32px",
    "@media (max-height: 800px)": {
      marginBottom: "16px",
    },
  },
  primaryButton: {
    height: "52px !important",
    backgroundColor: "#062133",
    color: "#FFFFFF",
    padding: "15px 20px",
    borderRadius: "10px",
    width: "100%",
    fontSize: "18px",
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
    "&:hover": {
      backgroundColor: "#051a29",
    },
    "@media (max-height: 800px)": {
      height: "40px !important",
    },
  },
  secondaryButton: {
    height: "52px !important",
    color: "#062133",
    border: "1.5px solid #062133 !important",
    borderRadius: "10px",
    width: "100%",
    fontSize: "18px !important",
    padding: "0px",
    fontWeight: 700,
    fontFamily: "Inter, sans-serif",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "rgba(6, 33, 51, 0.04)",
    },
    "& div > span": {
      fontWeight: 700,
    },
    "@media (max-height: 800px)": {
      height: "40px !important",
    },
  },
  title: {
    color: "#062133",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing.sm,
    fontFamily: "Inter, sans-serif",
    fontSize: "24px",
    "@media (max-height: 800px)": {
      marginBottom: 0,
    },
  },
  subtitle: {
    color: "#062133",
    marginBottom: theme.spacing.sm,
    fontWeight: "bold",
    textAlign: "center",
    fontSize: "18px",
    fontFamily: "Inter, sans-serif",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    fontSize: "16px",
    color: "#384D5C",
    fontFamily: "Inter, sans-serif",
  },
  listIcon: {
    marginRight: theme.spacing.sm,
    color: theme.colors.green[6],
  },
  instructionsBox: {
    maxWidth: "350px",
  },
  description: {
    fontSize: "16px",
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    marginBottom: theme.spacing.sm,
    lineHeight: "20px",
  },
  instructionList: {
    width: "100%",
    fontFamily: "Inter, sans-serif",
    "& .mantine-List-item:not(:first-of-type)": {
      marginTop: "2px !important",
    },
  },
}));

interface LoginCardProps {
  children: React.ReactNode;
  className?: string;
}

interface LogoProps {
  src?: string;
  alt?: string;
}

interface TitleProps {
  children: React.ReactNode;
}

interface SubTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface DescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface InstructionsProps {
  description: string;
  items: string[];
}

interface PrimaryButtonProps {
  onClick: () => void;
  children: React.ReactNode;
}

interface SecondaryButtonProps {
  onClick: () => void;
  children: React.ReactNode;
}

const LoginCardRoot: React.FC<LoginCardProps> = ({
  children,
  className = "",
}) => {
  const { classes, cx } = useStyles();

  return (
    <Paper className={cx(classes.loginCard, className)} withBorder>
      {children}
    </Paper>
  );
};

const Logo: React.FC<LogoProps> = ({
  src = JEM_LOGO_URL,
  alt = "Jem HR Logo",
}) => {
  const { classes } = useStyles();

  return (
    <Center className={classes.logoContainer}>
      <Box>
        <img src={src} alt={alt} className={classes.logo} />
      </Box>
    </Center>
  );
};

const LoginTitle: React.FC<TitleProps> = ({ children }) => {
  const { classes } = useStyles();

  return (
    <Center mb="sm">
      <Title order={1} className={classes.title}>
        {children}
      </Title>
    </Center>
  );
};

const SubTitle: React.FC<SubTitleProps> = ({ children, className }) => {
  const { classes, cx } = useStyles();

  return (
    <Title order={2} className={cx(classes.subtitle, className)}>
      {children}
    </Title>
  );
};

const Description: React.FC<DescriptionProps> = ({ children, className }) => {
  const { classes, cx } = useStyles();

  return <Text className={cx(classes.description, className)}>{children}</Text>;
};

const Instructions: React.FC<InstructionsProps> = ({ description, items }) => {
  const { classes } = useStyles();

  return (
    <Box mb="xl">
      <Box className={classes.instructionsBox} mx="auto">
        <Text className={classes.description} mb="sm">
          {description}
        </Text>

        <List spacing="xs" size="md" className={classes.instructionList}>
          {items.map((item, index) => (
            <List.Item key={item}>
              <Text color="dimmed" className={classes.listItem}>
                {index + 1}. {item}
              </Text>
            </List.Item>
          ))}
        </List>
      </Box>
    </Box>
  );
};

const PrimaryButton: React.FC<PrimaryButtonProps> = ({ onClick, children }) => {
  const { classes } = useStyles();

  return (
    <Box mb="sm">
      <Button
        className={classes.primaryButton}
        onClick={onClick}
        fullWidth
        size="lg"
      >
        {children}
      </Button>
    </Box>
  );
};

const SecondaryButton: React.FC<SecondaryButtonProps> = ({
  onClick,
  children,
}) => {
  const { classes } = useStyles();

  return (
    <Box>
      <Button
        className={classes.secondaryButton}
        variant="outline"
        onClick={onClick}
        fullWidth
        size="lg"
      >
        {children}
      </Button>
    </Box>
  );
};

export const LoginCard = Object.assign(LoginCardRoot, {
  Logo,
  Title: LoginTitle,
  SubTitle,
  Instructions,
  PrimaryButton,
  SecondaryButton,
  Description,
});

export default LoginCard;
